/**
 * Money is an integer number of Toman everywhere in the system (ADR-0002 / D3).
 * Rial only ever appears at a vendor boundary, where the driver multiplies by 10.
 */
export type Toman = number

const FA_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

export function toRial(toman: Toman): number {
  assertToman(toman)
  return toman * 10
}

export function fromRial(rial: number): Toman {
  if (!Number.isInteger(rial) || rial % 10 !== 0) {
    throw new RangeError(`rial amount ${rial} is not a whole number of Toman`)
  }
  return rial / 10
}

export function assertToman(value: number): asserts value is Toman {
  if (!Number.isInteger(value)) {
    throw new TypeError(`money must be an integer number of Toman, got ${value}`)
  }
}

/** `۱٬۲۵۰٬۰۰۰ تومان` — the storefront/admin display format. */
export function formatToman(value: Toman, options: { digits?: 'fa' | 'en'; suffix?: boolean } = {}): string {
  assertToman(value)
  const { digits = 'fa', suffix = true } = options
  const grouped = new Intl.NumberFormat('en-US').format(value)
  const rendered = digits === 'fa' ? toPersianDigits(grouped.replace(/,/g, "٬")) : grouped
  return suffix ? `${rendered} تومان` : rendered
}

export function toPersianDigits(input: string): string {
  return input.replace(/[0-9]/g, (d) => FA_DIGITS[Number(d)] as string)
}

export function toEnglishDigits(input: string): string {
  return input
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
}
